/**
 * Extracted prototype style library — read API.
 *
 * Each entry is a style **source**: `{slug, label, values}`, a name→value map
 * over the published theme vocabulary, generated by
 * scripts/extract-prototype-styles.mjs. `library/library.json` bundles them all
 * into one import, because the bench builds this with esbuild where
 * `import.meta.glob` does not exist; the per-style `<slug>.style.json` files
 * are the same sources at editable granularity.
 *
 * The source is not a wire format. A style IS a sheet (trace-viewer
 * `docs/STYLES.md`), and {@link styleSheet} compiles one on demand via
 * `createStyleSheet`, which types every value from the vocabulary — so the
 * standard validates these files rather than being bypassed by them. Keeping
 * the source terse is deliberate: a compiled sheet repeats a type, a kind and
 * an inherits flag per entry, all of which the vocabulary already decides, and
 * that made a 28-value style a 415-line file.
 *
 * A style states only what it CHANGES. An absent value is not a gap: an
 * undeclared property leaves the consumer's binding unresolved, so the
 * material's own authored value drives — which is what "this style doesn't
 * touch roughness" should mean.
 *
 * Metallic is the exception: every role with a material states it. The GLB's
 * own value is a factor times a texture, which no panel can show as one
 * number, so plastics read "Metallic 100%". `npm run styles:measure` fills in
 * the value that leaves the render unchanged.
 *
 * `styles-meta.json` is the AI-facing sidecar: mood, intent and staging
 * recommendations. Colour and typography are NOT there — they are values here,
 * now that the vocabulary pins those axes.
 *
 * Applying a style in the editor is `applyStyleSheet` (trace-viewer/editor)
 * with the compiled sheet's hosted URL. Callers that bake at placement rather
 * than referencing a sheet read {@link LibraryStyle.values} directly (see
 * `themedRenderer` in ai/tools/template-tools.ts).
 */

import type { PropertySheet } from 'trace-viewer/behaviors';
import { bundledFontFamilies, createStyleSheet, declaredFontFamilies, isFamilyAvailable } from 'trace-viewer/editor';

import libraryCatalog from './library/library.json';
import metaJson from './library/styles-meta.json';

/** A style's values: the name→value map over the published theme vocabulary. */
type StyleValues = Record<string, string | number | boolean>;

/** The bundled catalog of style sources. */
interface StyleCatalogFile {
    version: number;
    styles: { slug: string; label: string; values: StyleValues }[];
}

export interface StyleMeta {
    name: string;
    slug: string;
    description: string | null;
    summary: string | null;
    mood: string | null;
    recommendedSets: string[];
    visualStyle: unknown;
    aiMetadata: { description?: string; aiMoodNarrative?: string; aiIntent?: string; tags?: string[] } | null;
    defaultComposition: string | null;
    triplanarRoles: string[];
    emissiveRoles: string[];
}

export interface LibraryStyle {
    slug: string;
    label: string;
    /** What the style sets, over the published theme vocabulary. */
    values: StyleValues;
    meta: StyleMeta;
}

const META = metaJson as StyleMeta[];

// Through `unknown`: the JSON literal's inferred type is a 28-way union of
// object shapes (each style omits the values it doesn't set), which is exactly
// the terseness we want in the file and exactly what TS cannot narrow to one
// record type.
const CATALOG = libraryCatalog as unknown as StyleCatalogFile;

const SOURCES = new Map((CATALOG.styles ?? []).map(entry => [entry.label, entry]));

const STYLES: LibraryStyle[] = META.map(meta => {
    const source = SOURCES.get(meta.name);
    if (!source) throw new Error(`style source missing for meta entry "${meta.slug}"`);
    return { slug: meta.slug, label: source.label, values: source.values, meta };
});

const SHEET_CACHE = new Map<string, PropertySheet>();

/**
 * Compile a style source into the Canopy sheet a CMS `Style` asset carries.
 *
 * This is where the published vocabulary is applied: `createStyleSheet` types
 * each value from it, so a value the standard does not cover cannot silently
 * become a sheet declaration. Cached — the compile is pure, and callers
 * (upload, preview) ask repeatedly.
 */
export function styleSheet(style: LibraryStyle): PropertySheet {
    const cached = SHEET_CACHE.get(style.slug);
    if (cached) return cached;
    const { sheet, skipped } = createStyleSheet(style.values, style.label);
    if (skipped.length > 0) {
        throw new Error(`style "${style.slug}": no declaration shape for ${skipped.join(', ')}`);
    }
    SHEET_CACHE.set(style.slug, sheet);
    return sheet;
}

export function listStyles(): LibraryStyle[] {
    return STYLES;
}

export function getStyle(slug: string): LibraryStyle | null {
    return STYLES.find(s => s.slug === slug || s.meta.name.toLowerCase() === slug.toLowerCase()) ?? null;
}

/** Keyword search over name, mood, tags, and description. */
export function searchStyles(query?: string): LibraryStyle[] {
    if (!query?.trim()) return STYLES;
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return STYLES.filter(s => {
        const hay = [
            s.meta.name, s.meta.description, s.meta.summary, s.meta.mood,
            s.meta.aiMetadata?.aiIntent, s.meta.aiMetadata?.tags?.join(' '),
        ].filter(Boolean).join(' ').toLowerCase();
        return terms.some(t => hay.includes(t));
    });
}

/**
 * Map a font-family name to a family the engine draws. A family it has, bundled
 * or a hosted pack this deployment declared (src/host-font-packs.ts), passes
 * through in the engine's spelling. Anything else maps to the nearest bundled
 * MSDF family (trace-viewer ships 7) by keyword: condensed/display sans →
 * Oswald, serifs → Source Serif 4, monos → JetBrains Mono, everything
 * grotesk/geometric/humanist → Inter. Tolerates a full CSS stack as well as a
 * bare family name — the sheets carry the bare name, since a 3D text renderer
 * cannot honour a stack.
 */
export function mapFontToBundled(stack: string | undefined): string | undefined {
    if (!stack) return undefined;
    const drawn = engineFontFamily(stack);
    if (drawn) return drawn;
    const f = stack.toLowerCase();
    if (/(mono|jetbrains|plex mono|space mono|courier)/.test(f)) return 'JetBrains Mono';
    if (/(serif|playfair|lora|georgia|garamond|fraunces)/.test(f) && !f.includes('sans')) return 'Source Serif 4';
    if (/(alumni|oswald|bebas|archivo narrow|condensed|barlow condensed|anton)/.test(f)) return 'Oswald';
    if (/(bungee|display)/.test(f)) return 'Bungee';
    if (/(caveat|hand|script)/.test(f)) return 'Caveat';
    if (/(inter|grotesk|urbanist|public sans|nunito|manrope|work sans|dm sans|figtree|sora|outfit)/.test(f)) return 'Inter';
    return 'Roboto';
}

/**
 * The engine's spelling of the stack's first family, when the engine draws it:
 * bundled, or declared by a hosted pack whose faces it will fetch on first use.
 * Asked at call time, because packs are declared when the manifest arrives,
 * after this module loads.
 */
function engineFontFamily(stack: string): string | undefined {
    const name = stack.split(',')[0].trim().replace(/["']/g, '').toLowerCase();
    return bundledFontFamilies().find(family => family.toLowerCase() === name)
        ?? declaredFontFamilies().find(family => family.toLowerCase() === name && isFamilyAvailable(family));
}

/**
 * Heading/body families for a style, as {@link mapFontToBundled} resolves them
 * (undefined roles fall through).
 *
 * Read from the style's `titleFont` / `bodyFont` values — the standardized
 * text axis (Studio spec: Title / Header / Body) — rather than from a sidecar.
 */
export function styleTypography(style: LibraryStyle): { heading?: string; body?: string } {
    const family = (name: string): string | undefined => {
        const value = style.values[name];
        return typeof value === 'string' ? mapFontToBundled(value) : undefined;
    };
    return { heading: family('titleFont'), body: family('bodyFont') };
}

/**
 * The four-role palette `place_template` accepts — the bridge from a styled look
 * to a templated canvas.
 *
 * Drawn from the standardized surface roles: `panel` is the surface a template
 * lays content on, and `text` is the *paired* foreground for the background
 * rather than an independently chosen colour, which is the whole point of a
 * surface carrying its own text colour.
 */
export function styleToTemplatePalette(style: LibraryStyle): { background?: string; surface?: string; accent?: string; text?: string } {
    const color = (name: string): string | undefined => {
        const value = style.values[name];
        return typeof value === 'string' && value ? value : undefined;
    };
    return {
        background: color('backgroundColor'),
        surface: color('panelColor'),
        accent: color('accentColor'),
        text: color('backgroundTextColor'),
    };
}
